import React, { useState, useEffect, useRef, useCallback } from 'react';
import MessageBubble from './MessageBubble';
import useConversation from '@/hooks/useConversation';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import LegalLinks from '@/components/LegalLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes

const SpeakerMutedIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const SpeakerOnIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M15.54 8.46a5 5 0 010 7.07" />
    <path d="M19.07 4.93a10 10 0 010 14.14" />
  </svg>
);

const ConversationView = ({ conversationId, onConversationUpdate, onMenuOpen, onClose }) => {
  const {
    messages,
    isLoading,
    streamingMessage,
    sendMessage,
    loadMessages,
    setOnResponseComplete,
  } = useConversation(conversationId);
  
  const [inputValue, setInputValue] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showClosingMessage, setShowClosingMessage] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [showVoiceHint, setShowVoiceHint] = useState(() => {
    return !localStorage.getItem('eunoia_voice_hint_seen');
  });
  const [voiceInputMode, setVoiceInputMode] = useState(() => {
    return localStorage.getItem('eunoia_voice_mode') || 'tap';
  });
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const [showNewMessagePill, setShowNewMessagePill] = useState(false);
  const prevMessageCountRef = useRef(0);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const audioContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const audioQueueRef = useRef([]); // Queue of AudioBuffers to play
  const isPlayingRef = useRef(false);

  // Voice input
  const voiceInput = useVoiceInput();
  const [voiceInputActive, setVoiceInputActive] = useState(false);

  // Hands-free mode
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showStillHere, setShowStillHere] = useState(false);
  const onPlaybackDoneRef = useRef(null);
  const playbackDoneTimerRef = useRef(null);
  const currentGainRef = useRef(null);
  const stillHereTimerRef = useRef(null);

  // Smart interruption state
  const [interruptionActive, setInterruptionActive] = useState(false);
  const pendingInterjectionRef = useRef(null);

  // ─── Fade out Eunoia's voice (full stop) — used by mic toggle & hands-free ───
  const fadeOutVoiceFull = useCallback((duration = 0.4) => {
    audioQueueRef.current = [];
    if (currentSourceRef.current && currentGainRef.current) {
      try {
        const ctx = ensureAudioContext();
        currentGainRef.current.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
        setTimeout(() => {
          try { currentSourceRef.current?.stop(); } catch {}
          currentSourceRef.current = null;
          currentGainRef.current = null;
          isPlayingRef.current = false;
        }, (duration + 0.05) * 1000);
      } catch {
        stopVoice();
      }
    } else {
      stopVoice();
    }
  }, []);

  const handleMicToggle = useCallback(() => {
    if (voiceInput.isListening) {
      voiceInput.stopListening();
      setVoiceInputActive(false);
    } else {
      // If Eunoia is speaking, stop audio immediately
      if (isPlayingRef.current) {
        fadeOutVoiceFull(0.3);
        if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
        setInterruptionActive(false);
      }
      setVoiceInputActive(true);
      voiceInput.startListening((finalText) => {
        setVoiceInputActive(false);
        if (finalText.trim()) {
          setInputValue(finalText.trim());
        }
      });
    }
  }, [voiceInput, fadeOutVoiceFull]);

  // Persist voice input mode
  useEffect(() => {
    localStorage.setItem('eunoia_voice_mode', voiceInputMode);
  }, [voiceInputMode]);

  const initiateClosingRitual = useCallback(() => {
    if (isClosing) return;

    setIsClosing(true);

    setTimeout(() => {
      setShowClosingMessage(true);
    }, 2000);

    setTimeout(() => {
      if (onClose) onClose();
    }, 6000);
  }, [isClosing, onClose]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    if (messages.length > 0) {
      inactivityTimerRef.current = setTimeout(() => {
        initiateClosingRitual();
      }, INACTIVITY_TIMEOUT);
    }
  }, [messages.length, initiateClosingRitual]);

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading || isClosing) return;

    resetInactivityTimer();
    let message = inputValue.trim();

    // Prepend any pending interjection context from a short interruption
    if (pendingInterjectionRef.current) {
      message = `[user interjected: "${pendingInterjectionRef.current}"]\n${message}`;
      pendingInterjectionRef.current = null;
    }

    setInputValue('');

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    await sendMessage(message);
    onConversationUpdate();
  }, [inputValue, isLoading, isClosing, resetInactivityTimer, sendMessage, onConversationUpdate]);

  // Auto-send after voice input captures final text
  const pendingVoiceSendRef = useRef(false);
  useEffect(() => {
    if (!voiceInputActive && inputValue.trim() && pendingVoiceSendRef.current) {
      pendingVoiceSendRef.current = false;
      const t = setTimeout(() => {
        handleSendMessage();
      }, 400);
      return () => clearTimeout(t);
    }
  }, [voiceInputActive, inputValue, handleSendMessage]);

  useEffect(() => {
    if (voiceInputActive) {
      pendingVoiceSendRef.current = true;
    }
  }, [voiceInputActive]);

  // ─── Hands-free: soft listening tone ───
  const playListeningTone = useCallback(() => {
    return new Promise((resolve) => {
      try {
        const ctx = ensureAudioContext();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = 528;
        gain.gain.setValueAtTime(0, ctx.currentTime);
        gain.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.15);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.8);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.85);
        setTimeout(resolve, 900);
      } catch {
        resolve();
      }
    });
  }, []);

  // ─── Hands-free: start full listening (mic ON) ───
  const startHandsFreeListening = useCallback(() => {
    setVoiceInputActive(true);
    setShowStillHere(false);
    voiceInput.startListening((finalText) => {
      setVoiceInputActive(false);
      if (finalText.trim()) {
        setInputValue(finalText.trim());
      }
    });
  }, [voiceInput]);

  // ─── Hands-free: after audio finishes → 800ms buffer → tone → mic ON ───
  useEffect(() => {
    onPlaybackDoneRef.current = async () => {
      // 800ms buffer silence for echo cancellation
      await new Promise(r => setTimeout(r, 800));
      await playListeningTone();
      startHandsFreeListening();
    };
  }, [playListeningTone, startHandsFreeListening]);

  // ─── Hands-free: no-speech timeout → deactivate mic, show "still here" ───
  useEffect(() => {
    voiceInput.setOnNoSpeech(() => {
      setVoiceInputActive(false);
      setShowStillHere(true);
      if (stillHereTimerRef.current) clearTimeout(stillHereTimerRef.current);
      stillHereTimerRef.current = setTimeout(() => setShowStillHere(false), 4000);
      // Mic stays OFF — user can reactivate via talk button
    });
  }, [voiceInput]);

  // ─── Hands-free: interruption via speech detection during playback ───
  useEffect(() => {
    voiceInput.setOnSpeechDetected(() => {
      // User spoke while Eunoia is playing — mute immediately, activate mic
      setInterruptionActive(true);
      fadeOutVoiceFull(0.3);
      if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
      setVoiceInputActive(true);
      setTimeout(() => setInterruptionActive(false), 1000);
    });
  }, [voiceInput, fadeOutVoiceFull]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
      if (stillHereTimerRef.current) clearTimeout(stillHereTimerRef.current);
    };
  }, []);

  // Persist voice preference
  useEffect(() => {
    localStorage.setItem('eunoia_voice', voiceEnabled ? 'true' : 'false');
  }, [voiceEnabled]);

  // Auto-dismiss voice hint after 5 seconds
  useEffect(() => {
    if (!showVoiceHint) return;
    const timer = setTimeout(() => {
      setShowVoiceHint(false);
      localStorage.setItem('eunoia_voice_hint_seen', 'true');
    }, 5000);
    return () => clearTimeout(timer);
  }, [showVoiceHint]);

  // Ensure AudioContext is created and resumed on user gesture
  const ensureAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  };

  // Fetch streaming audio and decode to AudioBuffer
  const fetchAudioBuffer = async (text) => {
    const response = await fetch(`${API}/tts/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!response.ok) throw new Error(`TTS ${response.status}`);

    const arrayBuffer = await response.arrayBuffer();
    const ctx = ensureAudioContext();
    return await ctx.decodeAudioData(arrayBuffer);
  };

  // Play next buffer from the queue
  const playNextFromQueue = () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      currentGainRef.current = null;
      // Debounce: wait for more audio before signaling done
      if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
      playbackDoneTimerRef.current = setTimeout(() => {
        if (!isPlayingRef.current && audioQueueRef.current.length === 0) {
          onPlaybackDoneRef.current?.();
        }
      }, 600);
      return;
    }

    if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
    isPlayingRef.current = true;
    const buffer = audioQueueRef.current.shift();
    const ctx = ensureAudioContext();

    const source = ctx.createBufferSource();
    source.buffer = buffer;

    const gainNode = ctx.createGain();
    if (!currentSourceRef.current) {
      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(1, ctx.currentTime + 0.4);
    } else {
      gainNode.gain.setValueAtTime(1, ctx.currentTime);
    }

    source.connect(gainNode);
    gainNode.connect(ctx.destination);

    currentSourceRef.current = source;
    currentGainRef.current = gainNode;
    source.onended = () => {
      currentSourceRef.current = null;
      currentGainRef.current = null;
      playNextFromQueue();
    };
    source.start(0);
  };

  // Enqueue an AudioBuffer and start playing if not already
  const enqueueAudio = (buffer) => {
    if (playbackDoneTimerRef.current) clearTimeout(playbackDoneTimerRef.current);
    audioQueueRef.current.push(buffer);
    if (!isPlayingRef.current) {
      playNextFromQueue();
    }
  };

  // Stop all voice playback
  const stopVoice = () => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch {}
      currentSourceRef.current = null;
    }
  };

  // Wire up complete callback — plays FULL text after streaming ends
  useEffect(() => {
    setOnResponseComplete((fullText) => {
      if (!voiceEnabled) return;
      if (!fullText) return;
      fetchAudioBuffer(fullText)
        .then(buffer => enqueueAudio(buffer))
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceEnabled]);

  // Stop audio when component unmounts
  useEffect(() => {
    return () => { stopVoice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleVoice = () => {
    const next = !voiceEnabled;
    if (next) {
      ensureAudioContext();
    } else {
      stopVoice();
    }
    setVoiceEnabled(next);
  };

  useEffect(() => {
    loadMessages();
    setIsMobile(window.innerWidth <= 768);
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => {
    resetInactivityTimer();
    return () => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [messages, resetInactivityTimer]);

  useEffect(() => {
    // Smart scroll: only auto-scroll if user is near bottom
    const newCount = messages.length;
    const hadNewMessage = newCount > prevMessageCountRef.current;
    prevMessageCountRef.current = newCount;

    if (isNearBottomRef.current) {
      scrollToBottom();
    } else if (hadNewMessage && messages[messages.length - 1]?.role === 'assistant') {
      setShowNewMessagePill(true);
    }
  }, [messages]);

  // Also scroll for streaming indicator
  useEffect(() => {
    if (streamingMessage && isNearBottomRef.current) {
      scrollToBottom();
    }
  }, [streamingMessage]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessagePill(false);
  };

  // Track scroll position to detect if user is near bottom
  const handleScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distanceFromBottom < 100;
    if (isNearBottomRef.current) {
      setShowNewMessagePill(false);
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e) => {
    setInputValue(e.target.value);
    resetInactivityTimer();
    
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleCloseSession = () => {
    initiateClosingRitual();
  };

  return (
    <div 
      className="conversation-container" 
      data-testid="conversation-view"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#080608',
        position: 'relative'
      }}
    >
      {/* Closing ritual overlay */}
      {isClosing && (
        <div
          className="closing-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: '#080608',
            backgroundImage: 'radial-gradient(ellipse at 50% 40%, rgba(62, 28, 18, 0.4) 0%, rgba(8, 6, 8, 0) 70%)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: showClosingMessage ? 1 : 0,
            transition: 'opacity 1s ease-in'
          }}
        >
          {showClosingMessage && (
            <div style={{ textAlign: 'center', animation: 'closing-fade-in 2s ease-out' }}>
              <p
                style={{
                  fontFamily: "'Fraunces', Georgia, serif",
                  fontSize: '22px',
                  fontWeight: 300,
                  fontStyle: 'italic',
                  color: 'rgba(255, 243, 225, 0.82)',
                  letterSpacing: '0.02em',
                  lineHeight: 1.6,
                  marginBottom: '24px'
                }}
              >
                Carry what felt true. I'll be here.
              </p>
              <p
                style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '12px',
                  fontWeight: 400,
                  letterSpacing: '0.1em',
                  textTransform: 'lowercase',
                  color: 'rgba(255, 243, 225, 0.3)'
                }}
              >
                session complete
              </p>
            </div>
          )}
        </div>
      )}

      {/* Conversation content wrapper */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          opacity: isClosing ? 0.2 : 1,
          transition: 'opacity 2s ease-out',
          pointerEvents: isClosing ? 'none' : 'auto'
        }}
      >
        {/* Header */}
        <div
          className="conversation-top-bar"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '52px',
            background: '#080608',
            borderBottom: '0.5px solid rgba(196, 137, 74, 0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            zIndex: 20,
          }}
        >
          {/* Left — hamburger */}
          <button
            onClick={onMenuOpen}
            data-testid="conversation-menu-btn"
            className="menu-btn"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              width: '44px',
              height: '44px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              color: 'rgba(255, 243, 225, 0.55)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>

          {/* Center — eunoia wordmark */}
          <span style={{
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '18px',
            fontWeight: 400,
            fontStyle: 'italic',
            color: 'rgba(255, 243, 225, 0.6)',
            letterSpacing: '0.04em',
            userSelect: 'none',
          }}>
            eunoia
          </span>

          {/* Right — info + infinity + voice toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={() => setShowInfoModal(true)}
              data-testid="info-btn"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(255, 243, 225, 0.30)',
                WebkitTapHighlightColor: 'transparent',
                padding: 0,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
            <span style={{
              fontFamily: 'Georgia, serif',
              fontSize: '20px',
              color: 'rgba(255, 243, 225, 0.4)',
              lineHeight: 1,
              padding: '0 6px',
              userSelect: 'none',
            }}>
              ∞
            </span>
            <button
              onClick={toggleVoice}
              data-testid="voice-toggle-btn"
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: voiceEnabled ? '#C4894A' : 'rgba(255, 243, 225, 0.35)',
                WebkitTapHighlightColor: 'transparent',
                transition: 'color 0.2s ease',
                padding: 0,
              }}
            >
              {voiceEnabled ? <SpeakerOnIcon /> : <SpeakerMutedIcon />}
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div 
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="messages-container"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'none',
            paddingTop: '68px',
            paddingLeft: '16px',
            paddingRight: '16px',
            paddingBottom: 'calc(120px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div style={{ maxWidth: '640px', margin: '0 auto' }}>
            {/* Empty state */}
            {messages.length === 0 && !streamingMessage && (
              <div 
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 'calc(100vh - 148px)',
                  textAlign: 'center'
                }}
              >
                <p
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: '24px',
                    fontWeight: 300,
                    fontStyle: 'italic',
                    color: 'rgba(255, 243, 225, 0.4)',
                    marginBottom: '16px'
                  }}
                >
                  …
                </p>
                <p
                  style={{
                    fontFamily: "'Fraunces', Georgia, serif",
                    fontSize: 'clamp(18px, 4.5vw, 22px)',
                    fontWeight: 300,
                    fontStyle: 'italic',
                    color: 'rgba(255, 243, 225, 0.25)'
                  }}
                >
                  No rush. I'm here.
                </p>
              </div>
            )}
            
            {/* Messages */}
            {messages.map((message, index) => (
              <MessageBubble 
                key={message.id || index} 
                message={message}
              />
            ))}
            
            {/* Typing indicator — three staggered dots */}
            {streamingMessage && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                borderLeft: '2px solid rgba(196, 137, 74, 0.4)',
                paddingLeft: '12px',
                marginBottom: '24px',
                height: '28px',
              }}>
                {[0, 1, 2].map(i => (
                  <div
                    key={i}
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: 'rgba(196, 137, 74, 0.5)',
                      animation: `typingDotPulse 1.2s ease-in-out ${i * 0.3}s infinite`,
                    }}
                  />
                ))}
              </div>
            )}
            
            {!streamingMessage && messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (
              <div style={{ display: 'none' }} /> 
            )}
            
            <div ref={messagesEndRef} style={{ height: '1px', flexShrink: 0 }} />
          </div>
        </div>

        {/* New message pill indicator */}
        {showNewMessagePill && (
          <button
            onClick={scrollToBottom}
            data-testid="new-message-pill"
            className="new-msg-pill"
            style={{
              position: 'fixed',
              bottom: '140px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 15,
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '13px',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: '0.10em',
              color: 'rgba(196, 137, 74, 0.80)',
              background: 'rgba(196, 137, 74, 0.12)',
              border: '1px solid rgba(196, 137, 74, 0.25)',
              borderRadius: '100px',
              padding: '8px 16px',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              animation: 'pill-fade-in 0.3s ease-out'
            }}
          >
            new message ↓
          </button>
        )}

        {/* Input area */}
        {/* "Still here" prompt — hands-free mode */}
        {showStillHere && (
          <div
            data-testid="still-here-prompt"
            className="still-here-fade"
            style={{
              position: 'fixed',
              bottom: '140px',
              left: 0,
              right: 0,
              textAlign: 'center',
              zIndex: 11,
              pointerEvents: 'none'
            }}
          >
            <p style={{
              fontFamily: "'Cormorant', 'Garamond', serif",
              fontSize: '16px',
              fontStyle: 'italic',
              color: 'rgba(255, 243, 225, 0.30)'
            }}>
              still here whenever you're ready
            </p>
          </div>
        )}

        {/* Hands-free privacy notice overlay */}
        {showPrivacyNotice && (
          <div
            className="handsfree-overlay-fade"
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(8, 6, 8, 0.85)',
              zIndex: 100,
              padding: '40px'
            }}
          >
            <p style={{
              fontFamily: "'EB Garamond', 'Garamond', serif",
              fontSize: '15px',
              color: 'rgba(255, 243, 225, 0.55)',
              textAlign: 'center',
              lineHeight: 1.8,
              maxWidth: '280px'
            }}>
              your voice is heard only<br />
              in this moment.<br />
              Nothing is stored beyond<br />
              this conversation.
            </p>
          </div>
        )}


        <div
          className="input-area"
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: '#080608',
            borderTop: '0.5px solid rgba(196, 137, 74, 0.2)',
            paddingTop: '16px',
            paddingLeft: '12px',
            paddingRight: '12px',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-end',
            gap: '8px',
            maxWidth: '640px',
            margin: '0 auto',
            minHeight: '80px',
          }}>

            {/* Mic button — left */}
            {voiceInput.isSupported && (
              <button
                onClick={handleMicToggle}
                data-testid="mic-btn"
                disabled={isLoading || isClosing}
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: voiceInput.isListening ? 'rgba(196, 137, 74, 0.12)' : 'transparent',
                  border: '0.5px solid rgba(196, 137, 74, 0.4)',
                  color: voiceInput.isListening ? '#C4894A' : 'rgba(196, 137, 74, 0.7)',
                  cursor: isLoading ? 'default' : 'pointer',
                  padding: 0,
                  opacity: isLoading ? 0.3 : 1,
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.2s ease, color 0.2s ease',
                  animation: voiceInput.isListening ? 'micGlow 1.5s ease-out infinite' : 'none',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="1.5"
                     strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="1" width="6" height="12" rx="3" />
                  <path d="M19 10v2a7 7 0 01-14 0v-2" />
                  <line x1="12" y1="19" x2="12" y2="23" />
                  <line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
            )}

            {/* Textarea — center */}
            <textarea
              ref={textareaRef}
              data-testid="message-input"
              value={voiceInput.isListening ? voiceInput.interimTranscript : inputValue}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              placeholder="say what's true…"
              disabled={isLoading || isClosing || voiceInput.isListening}
              rows={1}
              style={{
                flex: 1,
                background: 'rgba(255, 243, 225, 0.05)',
                border: '0.5px solid rgba(196, 137, 74, 0.2)',
                borderRadius: '20px',
                padding: '8px 14px',
                fontFamily: "'EB Garamond', Georgia, serif",
                fontSize: 'clamp(16px, 4vw, 18px)',
                fontWeight: 400,
                lineHeight: 1.5,
                color: voiceInput.isListening
                  ? 'rgba(255, 243, 225, 0.45)'
                  : 'rgba(255, 243, 225, 0.9)',
                resize: 'none',
                outline: 'none',
                minHeight: '56px',
                maxHeight: '120px',
                overflowY: 'auto',
                transition: 'color 0.2s ease',
              }}
            />

            {/* Send button — right, only when text present; spacer otherwise */}
            {inputValue.trim() && !isClosing && !voiceInput.isListening ? (
              <button
                onClick={handleSendMessage}
                disabled={isLoading}
                data-testid="send-message-btn"
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(196, 137, 74, 0.9)',
                  border: 'none',
                  color: '#080608',
                  cursor: 'pointer',
                  padding: 0,
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'background 0.2s ease',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5"
                     strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            ) : (
              <div style={{ width: '56px', flexShrink: 0 }} />
            )}

          </div>
        </div>
      </div>

      {/* Info modal — onboarding content, always available */}
      {showInfoModal && (
        <div
          data-testid="info-modal"
          onClick={() => setShowInfoModal(false)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(8, 6, 8, 0.88)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '540px',
              maxHeight: '80vh',
              overflowY: 'auto',
              background: '#0F0D0F',
              borderTop: '1px solid rgba(196, 137, 74, 0.15)',
              borderRadius: '20px 20px 0 0',
              padding: '28px 28px calc(28px + env(safe-area-inset-bottom, 0px))',
              animation: 'infoSlideUp 0.28s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            {/* Close */}
            <button
              onClick={() => setShowInfoModal(false)}
              data-testid="info-modal-close"
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'rgba(255, 243, 225, 0.35)',
                fontSize: '22px',
                lineHeight: 1,
                padding: '4px 8px',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              ×
            </button>

            {/* What this space is for */}
            <h2 style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '20px',
              color: 'rgba(255, 243, 225, 0.75)',
              marginTop: 0,
              marginBottom: '12px',
            }}>
              What this space is for
            </h2>
            <div style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '16px',
              color: 'rgba(255, 243, 225, 0.50)',
              lineHeight: 1.75,
              marginBottom: '8px',
            }}>
              <p style={{ margin: '0 0 8px' }}>Eunoia is a quiet place to slow down and listen inwardly.</p>
              <p style={{ margin: '0 0 8px' }}>It's not here to fix you, optimize you, or rush you forward.</p>
              <p style={{ margin: '0 0 8px' }}>It's here to reflect, to soften, and to help you notice what's already within you.</p>
            </div>
            <div style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '16px',
              color: 'rgba(255, 243, 225, 0.70)',
              lineHeight: 1.75,
              marginBottom: '28px',
            }}>
              <p style={{ margin: '0 0 4px' }}>You don't need the right words.</p>
              <p style={{ margin: '0 0 4px' }}>You don't need a goal.</p>
              <p style={{ margin: 0 }}>You don't need to be anywhere else.</p>
            </div>

            <div style={{ height: '1px', background: 'rgba(255, 243, 225, 0.07)', marginBottom: '28px' }} />

            {/* How to use */}
            <h2 style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '20px',
              color: 'rgba(255, 243, 225, 0.75)',
              marginTop: 0,
              marginBottom: '12px',
            }}>
              How to use Eunoia
            </h2>
            <div style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '16px',
              color: 'rgba(255, 243, 225, 0.50)',
              lineHeight: 1.75,
              marginBottom: '8px',
            }}>
              <p style={{ margin: '0 0 6px' }}>· Begin with whatever is present — thoughts, feelings, or uncertainty</p>
              <p style={{ margin: '0 0 6px' }}>· Use voice if it feels supportive, or keep things text-only</p>
              <p style={{ margin: '0 0 6px' }}>· Stay for a few moments or linger — both are enough</p>
              <p style={{ margin: 0 }}>· Ask for what you need: reflection, gentleness, or practical help</p>
            </div>
            <p style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontStyle: 'italic',
              fontSize: '16px',
              color: 'rgba(255, 243, 225, 0.55)',
              marginTop: '12px',
              marginBottom: '28px',
            }}>
              There's no right way to do this.
            </p>

            <div style={{ height: '1px', background: 'rgba(255, 243, 225, 0.07)', marginBottom: '28px' }} />

            {/* A gentle reminder */}
            <h2 style={{
              fontFamily: "'Cormorant', Georgia, serif",
              fontStyle: 'italic',
              fontWeight: 400,
              fontSize: '20px',
              color: 'rgba(255, 243, 225, 0.75)',
              marginTop: 0,
              marginBottom: '12px',
            }}>
              A gentle reminder
            </h2>
            <div style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '16px',
              color: 'rgba(255, 243, 225, 0.50)',
              lineHeight: 1.75,
            }}>
              <p style={{ margin: '0 0 8px' }}>Eunoia doesn't replace your life, your relationships, or professional care.</p>
              <p style={{ margin: '0 0 8px' }}>It's a companion for reflection — meant to support your own wisdom, not replace it.</p>
              <p style={{ margin: 0, color: 'rgba(255, 243, 225, 0.70)' }}>Everything you discover here already belongs to you.</p>
            </div>

            <div style={{ height: '1px', background: 'rgba(255, 243, 225, 0.07)', margin: '28px 0 20px' }} />
            <LegalLinks />
          </div>
        </div>
      )}

      <style>{`
        .messages-container::-webkit-scrollbar { width: 4px; }
        .messages-container::-webkit-scrollbar-track { background: transparent; }
        .messages-container::-webkit-scrollbar-thumb {
          background: rgba(196, 137, 74, 0.15);
          border-radius: 2px;
        }

        .input-area textarea::placeholder {
          color: rgba(255, 243, 225, 0.25);
          font-style: italic;
        }

        .menu-btn:active { color: rgba(196, 137, 74, 0.80) !important; }
        .new-msg-pill:active { background: rgba(196, 137, 74, 0.20) !important; }

        @keyframes closing-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes pill-fade-in {
          from { opacity: 0; transform: translateX(-50%) translateY(4px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        @keyframes typingDotPulse {
          0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
          30%           { opacity: 1;   transform: scale(1.1); }
        }

        @keyframes messageReveal {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        @keyframes micGlow {
          0%   { box-shadow: 0 0 0 0   rgba(196, 137, 74, 0.45); }
          70%  { box-shadow: 0 0 0 8px rgba(196, 137, 74, 0);    }
          100% { box-shadow: 0 0 0 0   rgba(196, 137, 74, 0);    }
        }

        @keyframes handsfree-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .handsfree-overlay-fade {
          animation: handsfree-fade-in 0.8s ease-out forwards;
        }

        @keyframes still-here-fade {
          0%   { opacity: 0; }
          15%  { opacity: 1; }
          75%  { opacity: 1; }
          100% { opacity: 0; }
        }
        .still-here-fade {
          animation: still-here-fade 4s ease-in-out forwards;
        }

        @keyframes infoSlideUp {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default ConversationView;
